import classNames from 'classnames';
import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { usePavilionsValue } from '../atoms/pavilionsData';
import { AppPavilionDataMap } from '../utils/getStandsDataMap';
import setImageBookingRooms from '../utils/setImageBookingRooms';
import { PAVILION_MAP_SLUG, sortPavilionRooms } from '../utils/sortPavilionRooms';
import { useActiveRoom, useSetActiveRoom } from '../utils/use-active-room';

type Props = React.HTMLAttributes<HTMLElement> & {
    onRoomClick?: (roomId: string) => void;
    slug: string;
    children: React.ReactElement;
};

const MapSectionRoomsWrapper = ({ onRoomClick, slug, children, ...props }: Props) => {
    const roomsEl = useRef<SVGSVGElement>(null);

    const setActiveRoomInStore = useSetActiveRoom();
    const activeRoom = useActiveRoom();

    const { pavilionData } = usePavilionsValue();
    const data = useMemo(() => {
        if (!pavilionData?.data?.[slug]) return [];
        const schemaData = pavilionData.data?.[slug];
        if (!schemaData) return [];

        const rooms = Object.values(schemaData).filter(
            (item): item is AppPavilionDataMap => item !== null && item !== undefined,
        );

        return slug === PAVILION_MAP_SLUG ? sortPavilionRooms(rooms) : rooms;
    }, [pavilionData.data, slug]);

    const bookingStands = useMemo(() => {
        return data.filter(
            (stand): stand is AppPavilionDataMap & { booking: NonNullable<AppPavilionDataMap['booking']> } =>
                stand !== null && stand !== undefined && stand.booking !== undefined && stand.booking !== null,
        );
    }, [data]);

    const slugs = useMemo(() => {
        return data.map((room) => (room?.booking ? room.slug : null)).filter((slug): slug is string => slug !== null);
    }, [data]);

    useEffect(() => {
        const rooms = roomsEl.current;
        if (!rooms) return;

        const handleRoomClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const roomGroup = target.closest<SVGGElement>('g[id^="room-"]');

            if (roomGroup && roomGroup.id.startsWith('room-')) {
                const roomId = roomGroup.id.replace('room-', '');
                const roomData = data.find((room) => room?.slug === roomId);

                if (slugs.includes(roomId) && roomData) {
                    if (onRoomClick) {
                        onRoomClick(roomId);
                    }

                    setActiveRoomInStore(roomData);
                }
            }
        };

        rooms.addEventListener('click', handleRoomClick);

        return () => {
            rooms.removeEventListener('click', handleRoomClick);
        };
    }, [data, slugs, onRoomClick, setActiveRoomInStore]);

    useEffect(() => {
        const currentRoomsEl = roomsEl.current;

        if (!currentRoomsEl) return;

        const roomsData = Array.from(currentRoomsEl.querySelectorAll('g[id^="room-"]') || []).map((element) => ({
            element,
            id: element.id.replace('room-', ''),
        }));

        const bookedRooms = roomsData
            .map((room) => {
                const matchingStand = bookingStands.find((stand) => stand.slug === room.id);
                return matchingStand ? { ...room, standData: matchingStand } : null;
            })
            .filter((room): room is NonNullable<typeof room> => room !== null);

        if (bookedRooms.length > 0) {
            setImageBookingRooms(bookedRooms);
        }

        return () => {
            bookedRooms.forEach((room) => {
                if (!room) return;
                const image = room.element.querySelector('image');
                if (image) image.remove();
            });
        };
    }, [bookingStands]);

    useEffect(() => {
        const currentRoomsEl = roomsEl.current;

        if (!currentRoomsEl) return;

        Array.from(currentRoomsEl.querySelectorAll('g[id^="room-"]')).forEach((el) => {
            const roomId = el.id.replace('room-', '');
            const isBooked = bookingStands.some((stand) => stand.slug === roomId);

            if (isBooked) {
                el.classList.add('booking');
            } else {
                el.classList.remove('booking');
            }
        });
    }, [bookingStands]);

    useEffect(() => {
        const currentRoomsEl = roomsEl.current;

        if (!currentRoomsEl) return;

        Array.from(currentRoomsEl.querySelectorAll('g[id^="room-"]')).forEach((el) => {
            el.classList.remove('is-active');
            if (activeRoom && activeRoom.slug && el.id === `room-${activeRoom.slug}`) {
                el.classList.add('is-active');
            }
        });

        return () => {
            if (currentRoomsEl) {
                Array.from(currentRoomsEl.querySelectorAll('g[id^="room-"]')).forEach((el) => {
                    el.classList.remove('is-active');
                });
            }
        };
    }, [activeRoom]);

    const childrenWithRef = children
        ? React.cloneElement(children, {
              ref: roomsEl,
              ...props,
          })
        : null;

    return <div className={classNames('map-section-rooms-wrapper', props.className)}>{childrenWithRef}</div>;
};

export default MapSectionRoomsWrapper;
